import moment from "moment-timezone";
import type { GetTransactionsResponse } from "../../../../types/transactions";
import { parseApiDateTime } from "../../../../utils/timeUtils";
import { canMarkArrived } from "../booking/guards";

/**
 * Pure model for the appointment drawer (desktop) and detail sheet (mobile):
 * facts, the request / arrival area, footer gating. Shared so the two
 * surfaces can't drift.
 */

/** "1 hr 15 min" / "45 min" / "2 hr". */
export const formatDurationLong = (mins: number): string => {
  const total = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
};

type Txn = Pick<GetTransactionsResponse, "transaction" | "line_items">;

const serviceTimes = (txn: Txn | null | undefined, timezone: string) => {
  const services = txn?.line_items?.booked_services ?? [];
  const starts = services
    .map((s) => s.start_dt)
    .filter((d): d is string => !!d)
    .map((d) => parseApiDateTime(d, timezone));
  const ends = services
    .map((s) => s.calc__end_dt)
    .filter((d): d is string => !!d)
    .map((d) => parseApiDateTime(d, timezone));
  return {
    start: starts.length ? moment.min(starts) : null,
    end: ends.length ? moment.max(ends) : null,
  };
};

/** The appointment's last service has ended (salon instants). */
export const appointmentEnded = (
  txn: Txn | null | undefined,
  timezone: string,
  now: Date = new Date(),
): boolean => {
  const { end } = serviceTimes(txn, timezone);
  return !!end && !end.isAfter(moment(now));
};

/** Statuses after which the appointment is closed (no no-show, no arrival). */
export const CLOSED_STATUSES: ReadonlySet<string> = new Set([
  "Completed",
  "No Show",
  "Declined",
  "Cancelled",
]);

const PRE_ARRIVAL = new Set(["Accepted", "Confirmed"]);

/** "Arrived at 2:00 PM" from `transaction.arrived_at`, else "Arrived". */
export const arrivedLabel = (
  txn: Txn | null | undefined,
  timezone: string,
): string => {
  const at = txn?.transaction?.arrived_at;
  if (!at) return "Arrived";
  const m = parseApiDateTime(at, timezone);
  return m.isValid() ? `Arrived at ${m.format("h:mm A")}` : "Arrived";
};

/** "Starts in 25 mins" / "Starting now" / "Started 10 mins ago". */
export const startsInCopy = (minutesUntil: number): string => {
  const n = Math.round(minutesUntil);
  const mins = (v: number) => `${v} min${v === 1 ? "" : "s"}`;
  if (n === 0) return "Starting now";
  return n > 0 ? `Starts in ${mins(n)}` : `Started ${mins(-n)} ago`;
};

export type ArrivalArea =
  | { kind: "request" }
  | { kind: "markArrived"; copy: string }
  | { kind: "arrived"; label: string }
  | null;

/**
 * The one request / arrival block the drawer shows, by priority:
 * Requested → approval card; Arrived → green strip (+ Undo); an Accepted /
 * Confirmed appointment inside the existing Arrived window (canMarkArrived:
 * the salon day, from 1h before start) that hasn't ended → Mark arrived.
 */
export const arrivalArea = (
  txn: Txn | null | undefined,
  timezone: string,
  now: Date = new Date(),
): ArrivalArea => {
  const status = txn?.transaction?.appointment_status;
  if (status === "Requested") return { kind: "request" };
  if (status === "Arrived") {
    return { kind: "arrived", label: arrivedLabel(txn, timezone) };
  }
  if (
    status &&
    PRE_ARRIVAL.has(status) &&
    canMarkArrived(txn, timezone, now) &&
    !appointmentEnded(txn, timezone, now)
  ) {
    const { start } = serviceTimes(txn, timezone);
    const until = start ? start.diff(moment(now), "minutes", true) : 0;
    return { kind: "markArrived", copy: startsInCopy(until) };
  }
  return null;
};

/**
 * Checkout opens the (unchanged) Appointment Details / checkout modal. It
 * reads "Checkout" while money is owed and "View details" once paid, so the
 * details page is always reachable. Void blocks nothing.
 */
export const checkoutLabel = (txn: Txn | null | undefined): string =>
  txn?.transaction?.transaction_status === "paid" ? "View details" : "Checkout";

/** The Scheduled messages row count: the list's count, else the embedded rows. */
export const scheduledMessagesCount = (
  txn: Pick<
    GetTransactionsResponse,
    "reminder_messages" | "reminder_messages_count"
  >,
): number => txn.reminder_messages_count ?? txn.reminder_messages?.length ?? 0;
